import React, { useCallback, useEffect, useState } from "react";

import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Modal } from "@/components/ui/Modal";
import { OptionDropdown, type DropdownOption } from "@/components/ui/OptionDropdown";
import { PanelHeaderAction } from "@/components/ui/PanelHeaderAction";
import { PanelList } from "@/components/ui/PanelList";
import { PanelListItem } from "@/components/ui/PanelListItem";
import { LinkIcon, RefreshIcon, SpinnerIcon, TrashIcon } from "@/components/icons";
import { WorkspacePanel } from "@/domains/Ideas/components/WorkspacePanel";

import {
  telegramService,
  TelegramChat,
  TelegramPurpose,
} from "./telegram.service";

const PURPOSE_LABELS: Record<TelegramPurpose, string> = {
  idea_capture: "Idea capture",
  admin: "Admin",
};

const PURPOSE_OPTIONS: DropdownOption<TelegramPurpose>[] = [
  { value: "admin", label: PURPOSE_LABELS.admin },
  { value: "idea_capture", label: PURPOSE_LABELS.idea_capture },
];

const chatTitle = (chat: TelegramChat): string => chat.title ?? "Untitled chat";

interface LinkByIdModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLink: (chatId: number, purpose: TelegramPurpose) => Promise<void>;
}

/** Centered create modal (DD-006) for linking a chat by its numeric id. */
function LinkByIdModal({ isOpen, onClose, onLink }: LinkByIdModalProps) {
  const [chatId, setChatId] = useState("");
  const [purpose, setPurpose] = useState<TelegramPurpose>("admin");
  const [error, setError] = useState<string | null>(null);
  const [isLinking, setIsLinking] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setChatId("");
      setPurpose("admin");
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = Number(chatId.trim());
    if (!Number.isInteger(parsed)) {
      setError("Enter a valid numeric chat id.");
      return;
    }
    setIsLinking(true);
    setError(null);
    try {
      await onLink(parsed, purpose);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to link chat");
    } finally {
      setIsLinking(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!isLinking) onClose();
      }}
      title="Link by ID"
      size="sm"
      bodyPadding
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1">
          <label htmlFor="telegram-chat-id-input" className="text-sm font-medium text-gray-900">
            ID chat
          </label>
          <input
            id="telegram-chat-id-input"
            value={chatId}
            onChange={(e) => setChatId(e.target.value)}
            placeholder="-1001234567890"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden text-sm"
            disabled={isLinking}
            autoFocus
          />
        </div>

        <div className="space-y-1">
          <span className="text-sm font-medium text-gray-900">Scopo</span>
          <OptionDropdown
            value={purpose}
            options={PURPOSE_OPTIONS}
            onChange={setPurpose}
            variant="input"
            portal
            disabled={isLinking}
          />
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isLinking}
            className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={isLinking || chatId.trim() === ""}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLinking ? <SpinnerIcon className="w-4 h-4" /> : <LinkIcon className="w-4 h-4" />}
            Link
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Telegram linked chats as a Setup panel (DD-047). Pending and linked
 * chats are flat PanelList rows; Refresh and Link by ID sit in the header.
 */
export function TelegramConnectCard() {
  const [collapsed, setCollapsed] = useState(false);
  const [linked, setLinked] = useState<TelegramChat[]>([]);
  const [pending, setPending] = useState<TelegramChat[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyChatId, setBusyChatId] = useState<number | null>(null);

  const [purposeByChat, setPurposeByChat] = useState<
    Record<number, TelegramPurpose>
  >({});

  const [showLinkById, setShowLinkById] = useState(false);

  const [toUnlink, setToUnlink] = useState<TelegramChat | null>(null);
  const [isUnlinking, setIsUnlinking] = useState(false);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await telegramService.listChats();
      setLinked(data.linked);
      setPending(data.pending);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load chats");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleLinkPending = async (chat: TelegramChat) => {
    setBusyChatId(chat.chatId);
    setError(null);
    try {
      await telegramService.linkChat({
        chatId: chat.chatId,
        purpose: purposeByChat[chat.chatId] ?? "admin",
      });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to link chat");
    } finally {
      setBusyChatId(null);
    }
  };

  const handleLinkById = async (chatId: number, purpose: TelegramPurpose) => {
    await telegramService.linkChat({ chatId, purpose, manual: true });
    await refresh();
  };

  const handleUnlinkConfirm = async () => {
    if (!toUnlink) return;
    setIsUnlinking(true);
    try {
      await telegramService.unlinkChat(toUnlink.chatId);
      setToUnlink(null);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to unlink chat");
    } finally {
      setIsUnlinking(false);
    }
  };

  return (
    <>
      <WorkspacePanel
        title="Telegram"
        actions={
          <>
            <PanelHeaderAction
              variant="secondary"
              label="Refresh"
              icon={<RefreshIcon className="w-4 h-4" />}
              onClick={() => void refresh()}
              disabled={isLoading}
            />
            <PanelHeaderAction
              label="Link by ID"
              icon={<LinkIcon className="w-4 h-4" />}
              onClick={() => setShowLinkById(true)}
            />
          </>
        }
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((c) => !c)}
        className={collapsed ? "" : "h-full"}
        loading={isLoading}
      >
        <div className="space-y-5">
          <p className="text-sm text-gray-600">
            Add the bot to a Telegram group, then link the group here. Linked groups
            are accepted instantly, no redeploy needed.
          </p>

          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
              Pending
            </h4>
            <PanelList
              items={pending}
              keyFor={(chat) => chat.chatId}
              empty={
                <p className="text-sm text-gray-400">
                  No pending chats. Add the bot to a group to see it here.
                </p>
              }
              renderItem={(chat) => (
                <PanelListItem
                  title={chatTitle(chat)}
                  meta={<span className="text-xs text-gray-500">{chat.chatId}</span>}
                  trailing={
                    <>
                      <OptionDropdown
                        value={purposeByChat[chat.chatId] ?? "admin"}
                        options={PURPOSE_OPTIONS}
                        onChange={(purpose) =>
                          setPurposeByChat((prev) => ({ ...prev, [chat.chatId]: purpose }))
                        }
                        align="right"
                        portal
                        disabled={busyChatId === chat.chatId}
                      />
                      <button
                        type="button"
                        onClick={() => void handleLinkPending(chat)}
                        disabled={busyChatId === chat.chatId}
                        className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50"
                      >
                        {busyChatId === chat.chatId ? (
                          <SpinnerIcon className="w-4 h-4" />
                        ) : (
                          <LinkIcon className="w-4 h-4" />
                        )}
                        Link
                      </button>
                    </>
                  }
                />
              )}
            />
          </section>

          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
              Linked
            </h4>
            <PanelList
              items={linked}
              keyFor={(chat) => chat.chatId}
              empty={<p className="text-sm text-gray-400">No linked chats yet.</p>}
              renderItem={(chat) => (
                <PanelListItem
                  title={chatTitle(chat)}
                  meta={
                    <span className="text-xs text-gray-500">
                      {chat.chatId} ·{" "}
                      {chat.purpose ? PURPOSE_LABELS[chat.purpose] : "no purpose"}
                    </span>
                  }
                  trailing={
                    <button
                      type="button"
                      onClick={() => setToUnlink(chat)}
                      className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700"
                    >
                      <TrashIcon className="w-4 h-4" />
                      Unlink
                    </button>
                  }
                />
              )}
            />
          </section>
        </div>
      </WorkspacePanel>

      <LinkByIdModal
        isOpen={showLinkById}
        onClose={() => setShowLinkById(false)}
        onLink={handleLinkById}
      />

      <ConfirmDialog
        isOpen={toUnlink !== null}
        onClose={() => setToUnlink(null)}
        onConfirm={handleUnlinkConfirm}
        title="Unlink chat"
        message={`Unlink "${
          toUnlink?.title ?? toUnlink?.chatId
        }"? The bot will stop accepting messages from it until re-linked.`}
        confirmText="Unlink"
        variant="danger"
        isLoading={isUnlinking}
      />
    </>
  );
}
